import React, { useState, useEffect } from 'react';
import { Palette, X } from 'lucide-react';
import './ThemePicker.css';

const themes = [
  {
    id: 'sage',
    name: 'Sophisticated Sage',
    primary: '#346751',
    secondary: '#C84B31',
    tertiary: '#D4A574',
  },
  {
    id: 'basic',
    name: 'Primary Basic',
    primary: '#007AFF',
    secondary: '#AF52DE',
    tertiary: '#FF9500',
  },
  {
    id: 'midnight',
    name: 'Midnight Slate',
    primary: '#715A5A',
    secondary: '#44444E',
    tertiary: '#D3DAD9',
  },
  {
    id: 'forest',
    name: 'Forest Ember',
    primary: '#5A9690',
    secondary: '#2F5755',
    tertiary: '#432323',
  },
  {
    id: 'golden',
    name: 'Golden Harvest',
    primary: '#FCB53B',
    secondary: '#84994F',
    tertiary: '#B45253',
  },
  {
    id: 'autumn',
    name: 'Autumn Breeze',
    primary: '#EC5228',
    secondary: '#3F7D58',
    tertiary: '#EF9651',
  },
  {
    id: 'coastal',
    name: 'Coastal Sunset',
    primary: '#EB5B00',
    secondary: '#143D60',
    tertiary: '#A0C878',
  },
  {
    id: 'garden',
    name: 'Garden Bloom',
    primary: '#FF9A9A',
    secondary: '#5F8B4C',
    tertiary: '#945034',
  },
  {
    id: 'twilight',
    name: 'Twilight Rose',
    primary: '#F6ACC8',
    secondary: '#AF8BAF',
    tertiary: '#584153',
  },
  {
    id: 'meadow',
    name: 'Meadow Mist',
    primary: '#B0C5A4',
    secondary: '#D37676',
    tertiary: '#EBC49F',
  },
  {
    id: 'crimson',
    name: 'Crimson Dusk',
    primary: '#E16A54',
    secondary: '#9F5255',
    tertiary: '#F39E60',
  },
  {
    id: 'electric',
    name: 'Electric Bloom',
    primary: '#D91656',
    secondary: '#EE66A6',
    tertiary: '#FFEB55',
  },
  {
    id: 'heritage',
    name: 'Heritage Oak',
    primary: '#E07A5F',
    secondary: '#1B4D3E',
    tertiary: '#F1E3D3',
  },
];

function ThemePicker({ isOpen, setIsOpen }) {
  const [currentTheme, setCurrentTheme] = useState('sage');
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    // Load saved theme from localStorage
    const savedTheme = localStorage.getItem('audra-theme');
    if (savedTheme) {
      setCurrentTheme(savedTheme);
      applyTheme(savedTheme);
    }
  }, []);

  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const applyTheme = (themeId) => {
    const theme = themes.find(t => t.id === themeId);
    if (!theme) return;

    const root = document.documentElement;

    // Generate color variations from primary
    const primaryHex = theme.primary;
    const darkerShades = generateShades(primaryHex);

    root.style.setProperty('--sage-primary', theme.primary);
    root.style.setProperty('--sage-darkest', darkerShades.darkest);
    root.style.setProperty('--sage-dark', darkerShades.dark);
    root.style.setProperty('--sage-light', darkerShades.light);
    root.style.setProperty('--sage-lightest', darkerShades.lightest);
    root.style.setProperty('--cream', theme.tertiary);
    root.style.setProperty('--cream-muted', adjustBrightness(theme.tertiary, -15));
  };

  const generateShades = (hex) => {
    return {
      darkest: adjustBrightness(hex, -40),
      dark: adjustBrightness(hex, -20),
      light: adjustBrightness(hex, 20),
      lightest: adjustBrightness(hex, 40),
    };
  };

  const adjustBrightness = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, Math.min(255, (num >> 16) + amt));
    const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
    const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  };

  const handleThemeSelect = (themeId) => {
    setCurrentTheme(themeId);
    applyTheme(themeId);
    localStorage.setItem('audra-theme', themeId);
  };

  return (
    <>
      {/* Floating button - hidden on mobile via CSS */}
      <div className={`theme-picker ${isScrolling ? 'hidden' : ''}`}>
        <button
          className="theme-picker-toggle"
          onClick={() => setIsOpen(true)}
          aria-label="Change theme"
          title="Change theme"
        >
          <Palette size={24} />
        </button>
      </div>

      {isOpen && (
        <div className="theme-picker-overlay" onClick={() => setIsOpen(false)}>
          <div className="theme-picker-modal" onClick={e => e.stopPropagation()}>
            <div className="theme-picker-header">
              <h3>Choose a Theme</h3>
              <button className="theme-picker-close" onClick={() => setIsOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <p className="theme-picker-subtitle">
              Preview our app's beautiful themes
            </p>
            <div className="theme-picker-grid">
              {themes.map(theme => (
                <button
                  key={theme.id}
                  className={`theme-option ${currentTheme === theme.id ? 'active' : ''}`}
                  onClick={() => handleThemeSelect(theme.id)}
                >
                  <div className="theme-colors">
                    <span
                      className="color-swatch primary"
                      style={{ backgroundColor: theme.primary }}
                    />
                    <span
                      className="color-swatch secondary"
                      style={{ backgroundColor: theme.secondary }}
                    />
                    <span
                      className="color-swatch tertiary"
                      style={{ backgroundColor: theme.tertiary }}
                    />
                  </div>
                  <span className="theme-name">{theme.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ThemePicker;
